import * as coreClient from "@azure/core-client";

export interface AddressSearchRequest {
  /** The partial address entered by the user to perform a address search. It can be any partial text, partial or full postcode. */
  searchText: string;
  /** The session identifier that was received in the previous response. This improves efficiency and lookup speeds. This should be included in case of any subsequent calls made to the Search endpoint. */
  session?: string;
}

export interface AddressSearchResponse {
  searchResults?: AddressSearchResult[];
  /** The number of matches found based on the information provided */
  count?: number;
  /** The session identifier that was received in the previous response. This improves efficiency and lookup speeds. This should be included in case of any subsequent calls made to the Search endpoint. */
  session?: string;
}

export interface AddressSearchResult {
  /** User Readable Address Information */
  label?: string;
  /** A Unique Identifier for the match that can be supplied to the DrillDown or Retrieve endpoints */
  value?: string;
  /** Indicates if this match is a container for other addresses (e.g. a town, street etc.). If this is true, the value should be supplied to the DrillDown endpoint to get the details of the addresses in the container record. If it is false, the value should be supplied to the Retrieve endpoint to get the full details of the address. */
  container?: boolean;
  /** User Readable Address Information */
  items?: number;
}

export interface ApiError {
  code?: number;
  shortDescription?: string;
  description?: string;
}

export interface SearchDrillDownRequest {
  /** The value property from the response of previous requests from either the Search or DrillDown methods should be supplied here. */
  value: string;
}

export interface AddressRetrieveRequest {
  /** The value property from the response of previous requests from either the Search or DrillDown methods should be supplied here. */
  value: string;
}

export interface AddressResponse {
  address?: Address;
  location?: Location;
}

export interface Address {
  /** Address Line 1 */
  line1?: string;
  /** Address Line 2 */
  line2?: string;
  /** Address Line 3 */
  line3?: string;
  /** Post Code */
  postcode?: string;
  /** Town or City */
  townCity?: string;
}

export interface Location {
  districtCode?: string;
  district?: string;
  wardCode?: string;
  ward?: string;
}

/** Known values of {@link HostOptions} that the service accepts. */
export enum KnownHostOptions {
  /** host: https://dt8nxbus.api.nationalexpress.com */
  HttpsDt8NxbusApiNationalexpressCom = "https://dt8nxbus.api.nationalexpress.com",
  /** host: https://perfnxbus.api.nationalexpress.com */
  HttpsPerfnxbusApiNationalexpressCom = "https://perfnxbus.api.nationalexpress.com",
  /** host: https://nxbus.api.nationalexpress.com */
  HttpsNxbusApiNationalexpressCom = "https://nxbus.api.nationalexpress.com"
}

/**
 * Defines values for HostOptions. \
 * {@link KnownHostOptions} can be used interchangeably with HostOptions,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **https:\/\/dt8nxbus.api.nationalexpress.com**: host: https:\/\/dt8nxbus.api.nationalexpress.com \
 * **https:\/\/perfnxbus.api.nationalexpress.com**: host: https:\/\/perfnxbus.api.nationalexpress.com \
 * **https:\/\/nxbus.api.nationalexpress.com**: host: https:\/\/nxbus.api.nationalexpress.com
 */
export type HostOptions = string;

/** Optional parameters. */
export interface AddressSearchOptionalParams
  extends coreClient.OperationOptions {
  /** Address Search Request */
  body?: AddressSearchRequest;
}

/** Contains response data for the addressSearch operation. */
export type AddressSearchOperationResponse = AddressSearchResponse;

/** Optional parameters. */
export interface AddressSearchDrillDownOptionalParams
  extends coreClient.OperationOptions {
  /** Address Search Drill Down Request */
  body?: SearchDrillDownRequest;
}

/** Contains response data for the addressSearchDrillDown operation. */
export type AddressSearchDrillDownResponse = AddressSearchResponse;

/** Optional parameters. */
export interface AddressRetrieveOptionalParams
  extends coreClient.OperationOptions {
  /** Address Retrieve Request */
  body?: AddressRetrieveRequest;
}

/** Contains response data for the addressRetrieve operation. */
export type AddressRetrieveResponse = AddressResponse;

/** Optional parameters. */
export interface AddressAPIOptionalParams
  extends coreClient.ServiceClientOptions {
  /** server parameter */
  $host?: HostOptions;
  /** Overrides client endpoint. */
  endpoint?: string;
}
